import { useState } from "react";
import { useSession } from "@core/session/SessionProvider";
import type { PointRule } from "@core/types";
import { tokens } from "@core/theme/tokens";
import { Badge } from "@portal/common";
import { RuleEditorModal } from "./RuleEditorModal";
import { RuleHistoryCard } from "./RuleHistoryCard";
import { usePointRules } from "./rulesQueries";
import {
  formatRuleDate,
  ruleTableCardStyle,
  RuleEditAction,
  RuleStatusBadge,
  RuleTableHeaderRow,
  RuleTableRow,
  RuleTableStates,
} from "./ruleTableChrome";

const COLUMNS = "48px 40px 1.6fr 130px 220px 132px";
const HEADERS = [
  "#",
  "",
  "Acción del referido",
  "Puntos",
  "Vigencia",
  "Estado",
];

export function ReferralRulesPanel({
  canEdit,
}: {
  canCreate: boolean;
  canEdit: boolean;
}) {
  const rules = usePointRules("referral");
  const { hasPermission } = useSession();
  // F1 (retry-category review_finding, 2026-09-07): mismo razonamiento que
  // MemberRulesPanel.tsx — "Editar" es el único disparador del modal donde
  // vive el toggle Activo/Inactivo (A5), así que se ofrece con canEdit o
  // canInactivate.
  const canOpenEditor =
    canEdit || hasPermission("points_engine", "canInactivate");
  const [editing, setEditing] = useState<PointRule | null>(null);

  const list = rules.data?.rules ?? [];
  const impact = rules.data?.impact;
  // Orden por `sortOrder` del catálogo (admin.jsx AdminMotorReferido ordena
  // por `accion(a.accionId).orden`; `PointRule.action.sortOrder` es el
  // equivalente del contrato).
  const ordered = [...list].sort(
    (a, b) => (a.action.sortOrder ?? 0) - (b.action.sortOrder ?? 0),
  );

  return (
    <div>
      <div style={{ ...ruleTableCardStyle, padding: 22, marginBottom: 18 }}>
        {/* A4 (human-resolution-spec-212.json): SIEMPRE el RulesImpact del
          servidor, nunca recalculado en el cliente; se refresca invalidando
          la query tras guardar. */}
        <div
          style={{
            font: `400 13.5px/1.6 ${tokens.font.ui}`,
            color: tokens.text.secondary,
          }}
        >
          Un referido que completa todas las acciones genera{" "}
          <b
            className="display"
            style={{
              fontSize: 19,
              color: tokens.gold.light,
              fontStyle: "normal",
            }}
          >
            {impact?.fullJourney ?? 0}
          </b>{" "}
          puntos al miembro que lo refirió.
        </div>
        <div
          style={{
            font: `400 11.5px ${tokens.font.ui}`,
            color: tokens.text.disabled,
            marginTop: 6,
          }}
        >
          Un referido que solo compra genera {impact?.purchaseOnly ?? 0} puntos.
        </div>
      </div>

      <div
        style={{ ...ruleTableCardStyle, marginBottom: 20, overflowX: "auto" }}
      >
        <div style={{ minWidth: 980 }}>
          <RuleTableHeaderRow columns={COLUMNS} headers={HEADERS} />
          <RuleTableStates
            isLoading={rules.isLoading}
            isError={rules.isError}
            isEmpty={!rules.isLoading && !rules.isError && ordered.length === 0}
          />
          {!rules.isLoading && !rules.isError
            ? ordered.map((rule) => (
                <RuleTableRow
                  key={rule.id}
                  columns={COLUMNS}
                  active={rule.active}
                >
                  <span
                    style={{
                      font: `600 12px ${tokens.font.ui}`,
                      color: tokens.text.tertiary,
                    }}
                  >
                    {rule.action.sortOrder ?? "—"}
                  </span>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: rule.action.color ?? tokens.surface.line2,
                    }}
                  />
                  <span
                    style={{
                      font: `600 12.5px ${tokens.font.ui}`,
                      color: tokens.text.primary,
                    }}
                  >
                    {rule.action.name}
                    {rule.action.isFinal ? (
                      <span style={{ marginLeft: 8, display: "inline-flex" }}>
                        <Badge tone="achievement">Gran Premio</Badge>
                      </span>
                    ) : null}
                  </span>
                  <span
                    style={{
                      font: `600 13px ${tokens.font.ui}`,
                      color: tokens.gold.light,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    +{rule.points}
                  </span>
                  <span
                    style={{
                      font: `400 11.5px ${tokens.font.ui}`,
                      color: tokens.text.tertiary,
                    }}
                  >
                    {formatRuleDate(rule.validFrom)}
                    {rule.validUntil
                      ? ` → ${formatRuleDate(rule.validUntil)}`
                      : " · indefinida"}
                  </span>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <RuleStatusBadge active={rule.active} />
                    <RuleEditAction
                      visible={canOpenEditor}
                      onEdit={() => setEditing(rule)}
                    />
                  </div>
                </RuleTableRow>
              ))
            : null}
        </div>
      </div>

      <RuleHistoryCard ruleIds={list.map((rule) => rule.id)} />

      {editing !== null ? (
        <RuleEditorModal
          scope="referral"
          rule={editing}
          onClose={() => setEditing(null)}
          canCreate={false}
          canEdit={canEdit}
        />
      ) : null}
    </div>
  );
}
